/**
 * MarketingTriggersPage — Phase 8 Prompt 10 implementation.
 *
 * Per audit 05E_TRIGGER_MANAGER.md (marketing mode — full CRUD).
 */

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PillarPageShell from "../shared/PillarPageShell";
import {
  ActionButton,
  StatusPill,
  StarterChooser,
  type StarterEntry,
} from "../../ops";
import { EmptyState } from "../../help";
import apiService from "../../../services/api";
import TriggerEditorDialog, { type EditableTrigger } from "../shared/TriggerEditorDialog";
import {
  anyPeriodScoped,
  fmtFires,
  fmtRate,
  rateColor,
  triggerFireView,
  type TriggerFireFields,
} from "../shared/triggerMetrics";

interface TriggerRow extends TriggerFireFields {
  id: string;
  trigger_key: string;
  event_type: string;
  event_source: string;
  template_key?: string;
  template_category?: string;
  kind?: "transactional" | "nurture";
  priority: number;
  is_enabled: boolean;
  status: string;
  times_triggered: number;
  times_sent_successfully: number;
  times_failed: number;
  last_triggered_at: string | null;
}

const MarketingTriggersPage: React.FC = () => {
  const navigate = useNavigate();
  const [triggers, setTriggers] = useState<TriggerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [chooserOpen, setChooserOpen] = useState(false);
  const [editing, setEditing] = useState<EditableTrigger | null>(null);

  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    (async () => {
      try {
        const res: any = await apiService.get("/api/admin/email-triggers");
        if (!mounted) return;
        const rows = res?.data?.triggers ?? res?.triggers ?? res?.data ?? [];
        const nurture = rows.filter(
          (r: TriggerRow) =>
            r.kind === "nurture" ||
            (r.kind == null &&
              !["auth", "payment", "system", "subscription"].includes(
                r.event_source,
              )),
        );
        setTriggers(nurture);
      } catch (e: any) {
        if (mounted) setError(e?.message ?? "Failed to load triggers");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [reloadKey]);

  const filteredRows = useMemo(() => {
    if (!search.trim()) return triggers;
    const q = search.toLowerCase();
    return triggers.filter(
      (r) =>
        r.trigger_key.toLowerCase().includes(q) ||
        r.event_type.toLowerCase().includes(q),
    );
  }, [triggers, search]);

  // True only once the backend actually ships fires_period; until then the
  // table must not claim any window.
  const periodScoped = useMemo(() => anyPeriodScoped(triggers), [triggers]);

  const [creating, setCreating] = useState<EditableTrigger | null>(null);
  const handleStarterSelect = (starter: StarterEntry) => {
    setChooserOpen(false);
    // Was: setComingSoonOpen(true) — a placeholder that shipped for months.
    // The backend POST route has existed all along; open the real editor.
    setCreating({
      id: "",
      trigger_name: (starter as any)?.title || (starter as any)?.name || "",
      description: (starter as any)?.description || "",
      event_type: (starter as any)?.event_type || "",
      is_enabled: true,
      status: "active",
      priority: "medium",
      target_audience: "all_users",
      delay_minutes: 0,
      cooldown_hours: 0,
      kind: "nurture",
    });
  };

  const handleToggle = async (row: TriggerRow) => {
    try {
      await apiService.patch(`/api/admin/email-triggers/${row.id}/toggle`, {
        is_enabled: !row.is_enabled,
      });
      setTriggers((prev) =>
        prev.map((t) =>
          t.id === row.id ? { ...t, is_enabled: !t.is_enabled } : t,
        ),
      );
    } catch (e: any) {
      window.alert(`Toggle failed: ${e?.message ?? "unknown error"}`);
    }
  };

  return (
    <PillarPageShell
      pillar="marketing"
      title="Marketing Triggers"
      subtitle="Engagement event → nurture template bindings. Respects frequency caps and quiet hours."
      helpTipId="trigger.kind.badge"
      rightActions={
        <ActionButton
          verb="duplicate"
          labelOverride="+ New Trigger"
          onClick={() => setChooserOpen(true)}
          showIcon={false}
          variantOverride="primary"
        />
      }
    >
      <Box sx={{ mb: 2 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by trigger_key or event_type…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} height={48} />
              ))}
            </Box>
          ) : filteredRows.length === 0 ? (
            <EmptyState
              surface="triggers.empty.marketing"
              onCreate={() => setChooserOpen(true)}
            />
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Event</TableCell>
                  <TableCell>Status</TableCell>
                  {/* Was "Fires (24h)". Nothing here was ever 24h-scoped — the
                      counters are lifetime unless the backend ships
                      fires_period (M5 wire contract), in which case the header
                      says so. */}
                  <TableCell align="right">
                    {periodScoped ? "Fires (period)" : "Fires (all time)"}
                  </TableCell>
                  <TableCell align="right">Success</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRows.map((row) => {
                  // A trigger that never fired has NO success rate. The old
                  // `: 100` painted it green at 100.0%.
                  const fireView = triggerFireView(row);
                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        >
                          {row.trigger_key}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary" }}
                        >
                          → {row.template_key ?? "(unbound)"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.event_type}
                          size="small"
                          sx={{ fontFamily: "monospace", fontSize: "0.7rem" }}
                        />
                      </TableCell>
                      <TableCell>
                        <StatusPill
                          status={row.is_enabled ? "active" : "paused"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {fmtFires(fireView.fires)}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          color: rateColor(fireView.successRate),
                          fontWeight: fireView.successRate == null ? 400 : 600,
                        }}
                      >
                        {fmtRate(fireView.successRate)}
                      </TableCell>
                      <TableCell align="right">
                        <Box
                          sx={{
                            display: "inline-flex",
                            gap: 0.5,
                            justifyContent: "flex-end",
                          }}
                        >
                          <ActionButton
                            verb="edit"
                            size="small"
                            showIcon={false}
                            onClick={() => setEditing(row as EditableTrigger)}
                          />
                          <ActionButton
                            verb={row.is_enabled ? "pause" : "resume"}
                            size="small"
                            showIcon={false}
                            onClick={() => handleToggle(row)}
                          />
                          <ActionButton
                            verb="send_test_to_me"
                            size="small"
                            showIcon={false}
                            onClick={() => {
                              window.alert(`Test fire: ${row.trigger_key}`);
                            }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <StarterChooser
        entityType="trigger"
        open={chooserOpen}
        onClose={() => setChooserOpen(false)}
        onSelect={handleStarterSelect}
      />

      <TriggerEditorDialog
        open={Boolean(editing)}
        trigger={editing}
        onClose={() => setEditing(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />
      <TriggerEditorDialog
        open={Boolean(creating)}
        trigger={creating}
        mode="create"
        onClose={() => setCreating(null)}
        onSaved={() => setReloadKey((k) => k + 1)}
      />

    </PillarPageShell>
  );
};

export default MarketingTriggersPage;
